import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { IconButton, CircularProgress, Tooltip } from '@mui/material'

import './CIconButton.css'
import Icon from './Icon'

const CIconButton = ({
    icon,
    iconType = 'far',
    onClick,
    disabled = false,
    type = 'button',
    autoFocus = false,
    color = 'default',
    size = 'medium',
    showLoader = false,
    loading = false,
    className = '',
    colorTheme = 'primary',
    tooltip = '',
    edge = false,
    ...props
}) => {
    const [buttonStates, setButtonStates] = useState({
        icon,
        iconType,
        className,
        disabled,
        color,
        size,
        showLoader,
        loading,
        colorTheme,
        type,
        autoFocus,
        tooltip,
        edge,
    })

    useEffect(() => {
        setButtonStates({
            icon,
            iconType,
            className,
            disabled,
            color,
            size,
            showLoader,
            loading,
            colorTheme,
            type,
            autoFocus,
            tooltip,
            edge,
        })
    }, [icon, iconType, className, disabled, color, size, showLoader, loading, colorTheme, type, autoFocus, tooltip, edge])

    const handleClick = (event) => {
        if (!buttonStates.disabled && !buttonStates.loading && onClick) {
            onClick(event)
        }
    }

    const isLoading = buttonStates.showLoader || buttonStates.loading

    const iconButton = (
        <div className={`c-icon-button color-theme-${buttonStates.colorTheme}`}>
            <IconButton
                className={`c-icon-button-element ${buttonStates.className}`}
                onClick={handleClick}
                disabled={buttonStates.disabled || isLoading}
                color={buttonStates.color}
                size={buttonStates.size}
                type={buttonStates.type}
                autoFocus={buttonStates.autoFocus}
                edge={buttonStates.edge}
                {...props}
            >
                {isLoading ? (
                    <CircularProgress
                        size={buttonStates.size === 'small' ? 16 : buttonStates.size === 'large' ? 24 : 20}
                        thickness={4}
                        color="inherit"
                        className="c-icon-button-spinner"
                    />
                ) : (
                    <Icon name={buttonStates.icon} />
                )}
            </IconButton>
        </div>
    )

    return buttonStates.tooltip ? (
        <Tooltip title={buttonStates.tooltip} arrow>
            {iconButton}
        </Tooltip>
    ) : (
        iconButton
    )
}

CIconButton.propTypes = {
    // Icon props
    icon: PropTypes.string.isRequired,
    iconType: PropTypes.oneOf(['far', 'fas', 'fal', 'fab']),

    // Basic button props
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    autoFocus: PropTypes.bool,

    // Material-UI IconButton props
    color: PropTypes.oneOf(['inherit', 'default', 'primary', 'secondary', 'error', 'info', 'success', 'warning']),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    edge: PropTypes.oneOf([false, 'start', 'end']),

    // Loading props
    showLoader: PropTypes.bool,
    loading: PropTypes.bool,

    // Styling props
    className: PropTypes.string,
    colorTheme: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'info']),

    // Tooltip
    tooltip: PropTypes.string,
}

export default CIconButton
